"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronRight, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { useFirebaseApp } from "@/firebase/provider";
import { getAnalytics, logEvent } from "firebase/analytics";
import { hasAnalyticsConsent } from "@/lib/analytics/consent";
import type { LeadOfferHookId } from "../../lib/profile/leadOfferHooks";

type LeadIntent = "pricing" | "availability" | "booking" | "consult" | "custom" | "interview" | "check-availability" | "make-offer" | "question";

type Props = {
  profileOwnerId: string;
  profileOwnerName: string;
  profileType?: string;
  whatsappNumber?: string;
  isOwner: boolean;
  leadOfferType?: "discount" | "free-consult" | "custom" | "available-now" | "interviewing" | "open-to-offers";
  leadOfferHookId?: LeadOfferHookId;
  leadOfferHeadline?: string;
  leadOfferDescription?: string;
  leadOfferLogoUrl?: string;
  leadWhatsappPrefillTemplate?: string;
  showVerifiedByZamzi: boolean;
  /** Server-issued, time-limited token binding this popup to the viewed profile. */
  leadFunnelToken?: string;
};

// Detect profile product type
const detectProductType = (profileType?: string): "stay" | "restaurant" | "fixit" | "jobseeker" | "business" => {
  const t = (profileType || "").toLowerCase();
  if (t.includes("stay") || t.includes("hospitality") || t.includes("accommodation")) return "stay";
  if (t.includes("restaurant") || t.includes("food") || t.includes("catering")) return "restaurant";
  if (t.includes("fix-it") || t.includes("fixit") || t.includes("contractor")) return "fixit";
  if (t.includes("job seeker") || t.includes("domestic")) return "jobseeker";
  return "business";
};

const POPUP_DELAY_MS = 7000;
const CONSENT_VERSION = "popia-v1";

const BUSINESS_INTENTS: Array<{ value: LeadIntent; label: string }> = [
  { value: "pricing", label: "Get A Quote" },
  { value: "availability", label: "Check Dates" },
  { value: "booking", label: "Let's do this!" },
  { value: "consult", label: "Let's have a Quick Chat" },
  { value: "custom", label: "I Have a Question" },
];

const STAY_INTENTS: Array<{ value: LeadIntent; label: string }> = [
  { value: "availability", label: "Check Availability" },
  { value: "pricing", label: "Get Nightly Rate" },
  { value: "booking", label: "Book This Stay" },
  { value: "custom", label: "I Have a Question" },
];

const RESTAURANT_INTENTS: Array<{ value: LeadIntent; label: string }> = [
  { value: "booking", label: "Book a Table" },
  { value: "availability", label: "Check Availability" },
  { value: "pricing", label: "View Menu & Pricing" },
  { value: "custom", label: "I Have a Question" },
];

const FIXIT_INTENTS: Array<{ value: LeadIntent; label: string }> = [
  { value: "booking", label: "Request a Job" },
  { value: "pricing", label: "Get a Quote" },
  { value: "availability", label: "Check Availability" },
  { value: "custom", label: "I Have a Question" },
];

const JOB_SEEKER_INTENTS: Array<{ value: LeadIntent; label: string }> = [
  { value: "interview", label: "Schedule Interview" },
  { value: "check-availability", label: "Check Availability" },
  { value: "make-offer", label: "Make an Offer" },
  { value: "question", label: "I Have a Question" },
];

const INTENT_BUTTON_STYLES: Record<LeadIntent, { active: string; inactive: string }> = {
  pricing: {
    active: "border-emerald-700 bg-emerald-600 text-white hover:bg-emerald-700",
    inactive: "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100",
  },
  availability: {
    active: "border-sky-700 bg-sky-500 text-white hover:bg-sky-600",
    inactive: "border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100",
  },
  booking: {
    active: "border-amber-600 bg-amber-400 text-slate-950 hover:bg-amber-500",
    inactive: "border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100",
  },
  consult: {
    active: "border-orange-700 bg-orange-500 text-white hover:bg-orange-600",
    inactive: "border-orange-200 bg-orange-50 text-orange-900 hover:bg-orange-100",
  },
  custom: {
    active: "border-pink-700 bg-pink-500 text-white hover:bg-pink-600",
    inactive: "border-pink-200 bg-pink-50 text-pink-900 hover:bg-pink-100",
  },
  interview: {
    active: "border-emerald-700 bg-emerald-600 text-white hover:bg-emerald-700",
    inactive: "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100",
  },
  "check-availability": {
    active: "border-sky-700 bg-sky-500 text-white hover:bg-sky-600",
    inactive: "border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100",
  },
  "make-offer": {
    active: "border-amber-600 bg-amber-400 text-slate-950 hover:bg-amber-500",
    inactive: "border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100",
  },
  question: {
    active: "border-pink-700 bg-pink-500 text-white hover:bg-pink-600",
    inactive: "border-pink-200 bg-pink-50 text-pink-900 hover:bg-pink-100",
  }
};

const onlyDigits = (value: string) => value.replace(/\D/g, "");

const buildDefaultMessage = (name: string, intent: LeadIntent, isJobSeeker: boolean) =>
  isJobSeeker
    ? `Hi, I found ${name} on Zamzi Connect and I am interested in discussing a role. Selected action: ${intent}. Please share your availability for a quick chat.`
    : `Hi, I found ${name} on Zamzi Connect. I am interested in ${intent}. Please share next steps, pricing, and availability when you can.`;

const buildUtmPayload = () => {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get("utm_source") || "",
    utmMedium: params.get("utm_medium") || "",
    utmCampaign: params.get("utm_campaign") || "",
    utmTerm: params.get("utm_term") || "",
    utmContent: params.get("utm_content") || "",
  };
};

export function PublicProfileLeadFunnelPopup({
  profileOwnerId,
  profileOwnerName,
  profileType,
  whatsappNumber,
  isOwner,
  leadOfferType,
  leadOfferHookId,
  leadOfferHeadline,
  leadOfferDescription,
  leadOfferLogoUrl,
  leadWhatsappPrefillTemplate,
  showVerifiedByZamzi,
  leadFunnelToken,
}: Props) {
  const { toast } = useToast();
  const firebaseApp = useFirebaseApp();
  const isJobSeeker = typeof profileType === "string" && (profileType.toLowerCase().includes("job seeker") || profileType.toLowerCase().includes("domestic"));
  const productType = detectProductType(profileType);
  const isStay = productType === "stay";
  const isRestaurant = productType === "restaurant";
  const isFixIt = productType === "fixit";

  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [intent, setIntent] = useState<LeadIntent>(isJobSeeker ? "interview" : "pricing");
  const [email, setEmail] = useState("");
  const [consentGiven, setConsentGiven] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const storageKey = `lead-funnel-seen:${profileOwnerId}`;

  const logAnalyticsEvent = useCallback(
    (eventName: string, params: Record<string, string | number | boolean | undefined>) => {
      if (!hasAnalyticsConsent()) return;
      try {
        const analytics = getAnalytics(firebaseApp);
        logEvent(analytics, eventName, params);
      } catch (error) {
        console.warn(`[lead-funnel] analytics failed for ${eventName}`, error);
      }
    },
    [firebaseApp],
  );

  useEffect(() => {
    if (isOwner || typeof window === "undefined") return;

    const alreadySeenThisSession = window.sessionStorage.getItem(storageKey) === "1";
    if (alreadySeenThisSession) {
      return;
    }

    const timer = window.setTimeout(() => {
      setIsVisible(true);
      logAnalyticsEvent("profile_lead_teaser_shown", {
        profile_id: profileOwnerId,
        profile_type: profileType || "unknown",
      });
    }, POPUP_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [isOwner, logAnalyticsEvent, profileOwnerId, profileType, storageKey]);

    const computedHeadline = useMemo(() => {
    if (leadOfferHeadline?.trim()) return leadOfferHeadline.trim();
    if (isJobSeeker) {
      if (leadOfferType === "available-now") return `${profileOwnerName} is Available Now`;
      if (leadOfferType === "interviewing") return "Currently Interviewing — Act Fast";
      if (leadOfferType === "open-to-offers") return "Open to the Right Offer";
      return `Hire ${profileOwnerName}`;
    }
    if (isStay) {
      if (leadOfferType === "discount") return "Special Rate Available Tonight";
      if (leadOfferType === "free-consult") return "Ask Us Anything Before You Book";
      return `Book Your Stay at ${profileOwnerName}`;
    }
    if (isRestaurant) {
      if (leadOfferType === "discount") return "Special Offer — Limited Seats";
      if (leadOfferType === "free-consult") return "Chat With Us Before You Book";
      return `Reserve at ${profileOwnerName}`;
    }
    if (isFixIt) {
      if (leadOfferType === "discount") return "Discounted Call-out Available";
      return `Get ${profileOwnerName} On The Job`;
    }
    if (leadOfferType === "discount") return "Exclusive Offer for New Clients";
    if (leadOfferType === "free-consult") return "Free Quick Consult Available";
    return `Chat with ${profileOwnerName}`;
  }, [leadOfferHeadline, leadOfferType, profileOwnerName, isJobSeeker, isStay, isRestaurant, isFixIt]);

  // The teaser is now whatever the owner wrote, or the owner's own headline.
  // Previously it was resolved from a fixed preset list, which put restaurant
  // restaurant-flavoured copy in front of Stay hosts and Fix-it contractors.
  // The card is opt-in, so an owner who switched it on has written a hook;
  // there is no sensible default to fall back to, and inventing one is exactly
  // the behaviour that made the feature look broken.
  const hookPreset = useMemo(() => {
    const written = leadOfferDescription?.trim();
    const fallback = leadOfferHeadline?.trim();
    const text = written || fallback || "";
    return { id: "owner-written", text };
  }, [leadOfferDescription, leadOfferHeadline]);

    const computedDescription = useMemo(() => {
    if (leadOfferDescription?.trim()) return leadOfferDescription.trim();
    if (isJobSeeker) return "Choose a hiring action below, connect directly on WhatsApp, and optionally get a follow-up email from Zamzi Connect.";
    if (isStay) return "Check availability, ask about rates, or book directly with the host on WhatsApp.";
    if (isRestaurant) return "Book a table, check the menu, or ask the restaurant a quick question via WhatsApp.";
    if (isFixIt) return "Request a job, get a quote, or check if this contractor is available in your area.";
    return "Choose what you need below, connect instantly on WhatsApp, and optionally get a friendly follow-up email from Zamzi Connect.";
  }, [isJobSeeker, isStay, isRestaurant, isFixIt, leadOfferDescription]);

  const whatsappHref = useMemo(() => {
    const number = onlyDigits(whatsappNumber || "");
    if (!number) return "";
    const template = (leadWhatsappPrefillTemplate || "").trim();
    const baseMessage = template || buildDefaultMessage(profileOwnerName, intent, isJobSeeker);
    const finalMessage = `${baseMessage}\n\nIntent: ${intent}`;
    return `https://wa.me/${number}?text=${encodeURIComponent(finalMessage)}`;
  }, [intent, leadWhatsappPrefillTemplate, profileOwnerName, whatsappNumber]);

  const dismissTeaser = () => {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(storageKey, "1");
    }
    setIsExpanded(false);
    setIsVisible(false);
  };

  const collapsePanel = () => {
    setIsExpanded(false);
  };

  const openPanel = () => {
    setIsVisible(true);
    setIsExpanded(true);
    logAnalyticsEvent("profile_lead_teaser_opened", {
      profile_id: profileOwnerId,
      selected_hook: hookPreset.id,
    });
  };

  const handleIntentClick = (nextIntent: LeadIntent) => {
    setIntent(nextIntent);
    logAnalyticsEvent("profile_lead_intent_selected", {
      profile_id: profileOwnerId,
      selected_intent: nextIntent,
    });
  };

  const handleWhatsappClick = () => {
    if (!whatsappHref) {
      toast({
        title: "WhatsApp unavailable",
        description: "This profile has no WhatsApp number yet.",
        variant: "destructive",
      });
      return;
    }

    logAnalyticsEvent("profile_lead_whatsapp_clicked", {
      profile_id: profileOwnerId,
      selected_intent: intent,
    });
    window.open(whatsappHref, "_blank", "noopener,noreferrer");
  };

  const handleEmailSubmit = async () => {
    if (!email.trim()) {
      toast({
        title: "Email needed",
        description: "Enter your email if you want us to send you the link.",
        variant: "destructive",
      });
      return;
    }
    if (!consentGiven) {
      toast({ title: "Consent required", description: "Please accept POPIA consent to continue.", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      const utm = buildUtmPayload();
      const response = await fetch("/api/profile-lead-funnel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          profileOwnerId,
          profileOwnerName,
          profileType,
          visitorEmail: email,
          selectedIntent: intent,
          consentGiven,
          consentVersion: CONSENT_VERSION,
          pagePath: typeof window !== "undefined" ? window.location.pathname : "",
          referrer: typeof document !== "undefined" ? document.referrer : "",
          leadFunnelToken,
          ...utm,
        }),
      });

      const result = (await response.json().catch(() => ({}))) as { success?: boolean; error?: string };
      if (!response.ok || !result.success) {
        throw new Error(result.error || "Failed to submit lead details.");
      }

      logAnalyticsEvent("profile_lead_email_opt_in_submitted", {
        profile_id: profileOwnerId,
        selected_intent: intent,
      });

      toast({
        title: "You are all set",
        description: "We will send a friendly follow-up email in about 24 hours.",
      });
      dismissTeaser();
    } catch (error) {
      toast({
        title: "Unable to submit",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

    // Pick intents based on product type
  const activeIntents = isJobSeeker
    ? JOB_SEEKER_INTENTS
    : isStay
      ? STAY_INTENTS
      : isRestaurant
        ? RESTAURANT_INTENTS
        : isFixIt
          ? FIXIT_INTENTS
          : BUSINESS_INTENTS;

  // Product-specific gradient colours
  const teaserGradient = isStay
    ? "from-sky-300 via-blue-200 to-cyan-200"
    : isRestaurant
      ? "from-orange-300 via-red-200 to-pink-200"
      : isFixIt
        ? "from-yellow-300 via-amber-200 to-orange-200"
        : isJobSeeker
          ? "from-blue-300 via-indigo-200 to-blue-200"
          : "from-amber-300 via-orange-200 to-pink-200";

  const ctaButtonColor = isStay
    ? "bg-sky-700 hover:bg-sky-800"
    : isRestaurant
      ? "bg-red-600 hover:bg-red-700"
      : isFixIt
        ? "bg-amber-600 hover:bg-amber-700"
        : isJobSeeker
          ? "bg-blue-700 hover:bg-blue-800"
          : "bg-orange-500 hover:bg-orange-600";

  const whatsappLabel = isJobSeeker
    ? "Contact Candidate Directly"
    : isStay
      ? "Chat with Host on WhatsApp"
      : isRestaurant
        ? "Chat with Restaurant on WhatsApp"
        : isFixIt
          ? "Chat with Contractor on WhatsApp"
          : "Chat Directly with Owner";

  const whatsappSubtitle = isJobSeeker
    ? "No recruiters, no middlemen. Just the real candidate."
    : isStay || isRestaurant || isFixIt
      ? "Direct message. No call centres. No bots."
      : "No bots, No call centers. Just a real person.";

  return (
    <aside
      aria-label={`Contact ${profileOwnerName} directly`}
      className="pointer-events-none fixed bottom-4 left-3 z-40 w-[min(20rem,calc(100vw-1.5rem))] sm:bottom-6 sm:left-6 sm:w-72"
      data-hook-id={hookPreset.id}
      data-profile-owner={profileOwnerName}
    >
      <div
        className={[
          "pointer-events-auto transform-gpu transition-all duration-500 ease-out",
          isVisible ? "translate-x-0 opacity-100" : "-translate-x-[110%] opacity-0",
        ].join(" ")}
      >
        {!isExpanded ? (
          <div className="overflow-hidden rounded-[22px] border border-slate-200 bg-white/96 shadow-[0_18px_48px_rgba(15,23,42,0.16)] backdrop-blur">
            <div className={`bg-gradient-to-r ${teaserGradient} px-3 py-3 text-slate-950`}>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-700">Before you go</p>
                  {/* Previously capped to two lines inside an
                      overflow-hidden parent, so anything longer was silently
                      cut and the teaser read as broken on narrow screens. */}
                  <p className="mt-1 break-words text-sm font-semibold leading-5">{hookPreset.text}</p>
                </div>
                <button
                  type="button"
                  aria-label="Dismiss lead card"
                  className="shrink-0 rounded-full border border-slate-400/50 bg-white/80 p-1 text-slate-700 transition hover:bg-white"
                  onClick={dismissTeaser}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="px-3 pb-3">
              <p className="sr-only">
                Contact {profileOwnerName} on Zamzi Connect for pricing, availability, bookings, consultations, and direct WhatsApp enquiries.
              </p>
              <div className="flex items-center gap-2">
                <div className="rounded-full bg-slate-900 p-1.5 text-white">
                  <Sparkles className="h-3.5 w-3.5" />
                </div>
                <Button type="button" aria-expanded={isExpanded} className={`h-9 flex-1 ${ctaButtonColor} text-white`} onClick={openPanel}>
                  Open
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
                ) : (
          <div className="max-h-[min(88vh,46rem)] overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_28px_70px_rgba(15,23,42,0.24)]">
            <div className="max-h-[min(88vh,46rem)] space-y-3 overflow-y-auto overscroll-contain p-4 sm:p-5 bg-white">
              <div className="flex items-start justify-between gap-2 border-b border-slate-100 pb-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                    {isJobSeeker ? "Hire Me" : isStay ? "Book Your Stay" : isRestaurant ? "Reserve a Table" : isFixIt ? "Request a Job" : "My Offer"}
                  </p>
                  <h2 className="mt-1 text-lg font-bold leading-tight text-slate-900">{computedHeadline}</h2>
                  <p className="mt-1.5 text-sm leading-5 text-slate-600">{computedDescription}</p>
                </div>
                <button
                  type="button"
                  aria-label="Close lead card"
                  className="rounded-full border border-slate-200 bg-white p-1.5 text-slate-600 transition hover:bg-slate-50"
                  onClick={collapsePanel}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {leadOfferLogoUrl ? (
                <img
                  src={leadOfferLogoUrl}
                  alt={`${profileOwnerName} logo`}
                  className="h-12 w-auto rounded-md border border-slate-100 bg-white p-1"
                  loading="lazy"
                />
              ) : null}

                            <div>
                <Label className="text-sm font-semibold text-slate-800">
                  {isJobSeeker ? "What are you looking to do?" : isStay ? "What do you need?" : isRestaurant ? "What are you looking for?" : isFixIt ? "What do you need?" : "What do you need help with?"}
                </Label>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {activeIntents.map((item) => (
                    <Button
                      key={item.value}
                      type="button"
                      variant="outline"
                      className={intent === item.value ? INTENT_BUTTON_STYLES[item.value].active : INTENT_BUTTON_STYLES[item.value].inactive}
                      onClick={() => handleIntentClick(item.value)}
                    >
                      {item.label}
                    </Button>
                  ))}
                </div>
              </div>

                            <div className="space-y-1">
                <Button type="button" className="w-full border border-green-900 bg-green-800 text-white hover:bg-green-900" onClick={handleWhatsappClick}>
                  {whatsappLabel}
                </Button>
                <p className="text-center text-xs font-medium text-slate-600">{whatsappSubtitle}</p>
              </div>

              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-base font-semibold text-slate-900">Too busy to chat now?</p>
                <div className="mt-3 space-y-3">
                  <p className="text-sm leading-6 text-slate-600">
                    {isJobSeeker
                      ? "We\u2019ve all been there \u2014 you find the right candidate then lose the tab. Drop your email and we\u2019ll send their contact details straight to your inbox."
                      : "We\u2019ve all been there, you find the perfect pro then lose the tab. Drop your email and I\u2019ll put their contact details straight in your inbox for safekeeping."}
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="lead-funnel-email">Email</Label>
                    <Input
                      id="lead-funnel-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                    />
                  </div>
                  <label className="flex items-start gap-2 text-xs text-slate-600">
                    <Checkbox
                      checked={consentGiven}
                      onCheckedChange={(checked) => setConsentGiven(Boolean(checked))}
                    />
                    <span>
                      I consent to Zamzi Connect processing my details for this follow-up under POPIA.
                    </span>
                  </label>
                  <div className="space-y-2">
                    <Button type="button" variant="outline" onClick={handleEmailSubmit} disabled={isSubmitting} className="w-full border-emerald-700 bg-emerald-500 text-white hover:bg-emerald-600 hover:text-white">
                      {isSubmitting ? "Sending..." : "Send me the link"}
                    </Button>
                    <p className="text-center text-xs font-medium text-slate-500">
                      No Spam just a human connection to get the job done. We send only ONE email.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 sticky bottom-0 bg-white pt-1">
                <Button type="button" variant="outline" className="flex-1 border-sky-300 bg-sky-400 text-sky-950 hover:bg-sky-500 hover:text-sky-950" onClick={collapsePanel}>
                  Close
                </Button>
                <Button type="button" variant="ghost" className="text-slate-500 hover:bg-slate-50 hover:text-slate-700" onClick={dismissTeaser}>
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
